import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "rounded-lg border border-gray-200 bg-white p-4" }
const _hoisted_2 = { class: "mb-3 flex flex-wrap items-start gap-2" }
const _hoisted_3 = { class: "min-w-0 flex-1" }
const _hoisted_4 = { class: "text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_5 = { class: "mt-1 text-xs text-gray-500" }
const _hoisted_6 = { class: "ml-auto flex items-center gap-3" }
const _hoisted_7 = {
  key: 0,
  class: "text-xs text-gray-400"
}
const _hoisted_8 = {
  key: 0,
  class: "py-8 text-center"
}
const _hoisted_9 = { class: "text-xs text-red-700" }
const _hoisted_10 = {
  key: 1,
  class: "flex items-center justify-center py-16"
}
const _hoisted_11 = {
  key: 3,
  class: "py-8 text-center text-xs italic text-gray-400"
}
const _hoisted_12 = {
  key: 4,
  class: "mt-2 text-xs text-gray-500"
}

import { computed } from 'vue';
import merge from 'lodash-es/merge';
import type { Options } from 'highcharts';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import Spinner from '@/components/ui/Spinner.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { ChartType, type ProductionGroup } from '@/types';
import type { PigTallyDay } from '@/types/SectionLiveKpi';
import { formatChartTooltipHeader } from './chartTooltipHeader';
import type { ProductionGroupTimelineRange } from './productionGroupTimeline';
import { productionGroupIntersectsTimeline } from './productionGroupTimeline';


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductionGroupTimelineChart',
  props: {
    tally: {},
    productionGroups: {},
    range: {},
    formStart: {},
    formEnd: {},
    editedProductionGroupId: {},
    suggestedStart: {},
    suggestionConflicts: { type: Boolean },
    isLoading: { type: Boolean },
    hasError: { type: Boolean }
  },
  emits: ["useSuggestedStart", "retry"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const stockDefaults = useDefaultChartOptions({}, { type: ChartType.Stock }, true);

const selectedPeriodRange = computed(() => {
    if (!props.formStart) return null;

    const start = i18n.global.d(Date.parse(props.formStart), 'short');
    const end = props.formEnd ? i18n.global.d(Date.parse(props.formEnd), 'short') : null;
    return end ? `${start} – ${end}` : start;
});

const plotBands = computed(() => {
    const priorPeriods = props.productionGroups
        .filter((productionGroup) => productionGroup.id !== props.editedProductionGroupId && productionGroupIntersectsTimeline(productionGroup, props.range))
        .map((productionGroup) => ({
            from: Math.max(Date.parse(productionGroup.start), Date.parse(props.range.from)),
            to: Math.min(Date.parse(productionGroup.end ?? props.range.to), Date.parse(props.range.to)),
            color: 'rgba(156, 163, 175, 0.16)',
            label: {
                text: productionGroup.name,
                verticalAlign: 'top',
                y: 12,
                style: { color: '#6B7280', fontSize: '10px', fontWeight: '500' },
            },
        }));

    if (!props.formStart) return priorPeriods;

    return [
        ...priorPeriods,
        {
            from: Math.max(Date.parse(props.formStart), Date.parse(props.range.from)),
            to: Math.min(Date.parse(props.formEnd || props.range.to), Date.parse(props.range.to)),
            color: 'rgba(37, 99, 235, 0.12)',
        },
    ];
});

const chartOptions = computed(() => {
    const base: Options = merge({}, stockDefaults.value);

    return merge(base, {
        chart: {
            height: 270,
            spacingLeft: 0,
            spacingRight: 0,
            backgroundColor: 'transparent',
            marginRight: 8,
        },
        exporting: { enabled: false },
        rangeSelector: { enabled: false },
        scrollbar: { enabled: false },
        navigator: { enabled: true, height: 28, margin: 8 },
        legend: { enabled: false },
        xAxis: {
            min: Date.parse(props.range.from),
            max: Date.parse(props.range.to),
            tickInterval: null,
            plotBands: plotBands.value,
            plotLines: props.suggestedStart
                ? [
                      {
                          value: Date.parse(props.suggestedStart),
                          color: '#2563EB',
                          dashStyle: 'Dash',
                          width: 1,
                          zIndex: 4,
                          label: {
                              text: i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.suggestedStart') as string,
                              rotation: 0,
                              y: 12,
                              style: { color: '#2563EB', fontSize: '10px' },
                          },
                      },
                  ]
                : [],
        },
        yAxis: {
            softMin: 0,
            allowDecimals: false,
        },
        tooltip: {
            shared: true,
            split: false,
            formatter(this: any): string {
                const header = `<span style="font-size: 10px">${formatChartTooltipHeader(this.x)}</span>`;
                const point = this.points?.[0];
                return `${header}<br/><span style="color:${point?.color}">●</span> ${point?.series?.name}: <b>${i18n.global.n(Math.round(point?.y ?? 0))}</b>`;
            },
        },
        series: [
            {
                type: 'line',
                name: i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.pigTally') as string,
                data: props.tally.map((point) => [Date.parse(point.date), point.pigs]),
                marker: { enabled: false },
                dataGrouping: { enabled: false },
            },
        ],
    });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h3", _hoisted_4, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.title')), 1),
        _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.description')), 1)
      ]),
      _createElementVNode("div", _hoisted_6, [
        (selectedPeriodRange.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(selectedPeriodRange.value), 1))
          : _createCommentVNode("", true),
        (_ctx.suggestedStart)
          ? (_openBlock(), _createElementBlock("button", {
              key: 1,
              type: "button",
              class: "shrink-0 rounded-lg border border-primary-200 bg-primary-50 px-2.5 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('useSuggestedStart', _ctx.suggestedStart)))
            }, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.useSuggestedStart')), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    (_ctx.hasError)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.loadFailed')), 1),
          _createElementVNode("button", {
            type: "button",
            class: "mt-2 text-xs font-semibold text-primary-700 hover:text-primary-800",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('retry')))
          }, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.retry')), 1)
        ]))
      : (_ctx.isLoading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createVNode(Spinner, { class: "h-6 w-6" })
          ]))
        : (_ctx.tally.length)
          ? (_openBlock(), _createBlock(GenericChart, {
              key: 2,
              options: chartOptions.value,
              expandable: false
            }, null, 8, ["options"]))
          : (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.empty')), 1)),
    (!_ctx.suggestedStart && !_ctx.isLoading && _ctx.tally.length)
      ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(_ctx.$t(
                    _ctx.suggestionConflicts
                        ? 'views.monitoring.productionGroups.modals.manageProductionGroup.timeline.suggestedStartConflicts'
                        : 'views.monitoring.productionGroups.modals.manageProductionGroup.timeline.noSuggestedStart'
                )), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})